import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

const getAttendanceRating = (percent) => {
  if (percent === null || percent === undefined) return null;
  if (percent >= 80) return { label: 'Excellent', style: 'bg-emerald-100 text-emerald-800 border border-emerald-300', bar: 'bg-emerald-500', tip: 'Outstanding attendance — keep it up!' };
  if (percent >= 65) return { label: 'Good', style: 'bg-sky-100 text-sky-800 border border-sky-300', bar: 'bg-sky-500', tip: 'Good attendance — a small push can make it excellent.' };
  if (percent >= 50) return { label: 'Average', style: 'bg-amber-100 text-amber-800 border border-amber-300', bar: 'bg-amber-400', tip: 'Average attendance — engage students more to improve.' };
  return { label: 'Poor', style: 'bg-rose-100 text-rose-800 border border-rose-300', bar: 'bg-rose-500', tip: 'Poor attendance — urgent attention needed to re-engage students.' };
};

const formatBatchSubjectStatus = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized) return 'Not set';
  return normalized
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getBatchSubjectStatusClassName = (status) => {
  const normalized = String(status || '').trim().toLowerCase();

  if (normalized === 'ongoing') {
    return 'border border-emerald-200 bg-emerald-50 text-emerald-700';
  }

  if (normalized === 'completed') {
    return 'border border-slate-200 bg-slate-100 text-slate-700';
  }

  if (normalized === 'not started') {
    return 'border border-amber-200 bg-amber-50 text-amber-700';
  }

  return 'border border-gray-200 bg-gray-100 text-gray-600';
};

const getResolvedBatchSubjectStatus = (item) => {
  const candidates = [
    item?.batchSubjectStatus,
    item?.status,
    item?.subjectStatus,
  ];

  for (const candidate of candidates) {
    const normalized = String(candidate || '').trim().toLowerCase();
    if (normalized) {
      return normalized;
    }
  }

  return null;
};

export default function StaffAverageAttendance() {
  const todayISO = new Date().toISOString().slice(0, 10);
  const firstOfMonthISO = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(firstOfMonthISO);
  const [endDate, setEndDate] = useState(todayISO);
  const [staffSearch, setStaffSearch] = useState('');
  const [staffAttendance, setStaffAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [imageErrors, setImageErrors] = useState({});

  const getStaffImageUrl = (staffObj) => {
    if (!staffObj?.staffImage || imageErrors[staffObj.staffId]) return null;
    return getImageURL(staffObj.staffImage);
  };

  const handleImageError = (staffId) => {
    setImageErrors((prev) => ({ ...prev, [staffId]: true }));
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get('/admin/dashboard/staff-attendance', {
        params: { startDate, endDate },
      });
      const sorted = (res.data.staff || []).sort(
        (a, b) => (b.avgAttendancePercent ?? -1) - (a.avgAttendancePercent ?? -1)
      );
      setStaffAttendance(sorted);
      setLoaded(true);
      setSelectedStaff(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load staff attendance data.');
    } finally {
      setLoading(false);
    }
  };

  const normalizedStaffSearch = staffSearch.trim().toLowerCase();
  const filteredStaffAttendance = normalizedStaffSearch
    ? staffAttendance.filter((staff) => String(staff.staffName || '').toLowerCase().includes(normalizedStaffSearch))
    : staffAttendance;

  // Backend already returns pre-grouped subjects (type: 'group' | 'solo')
  const buildDisplayItems = (subjects) => subjects;

  const downloadExcel = () => {
    const wb = XLSX.utils.book_new();

    filteredStaffAttendance.forEach((staff, index) => {
      const rows = [];
      // Header info
      rows.push(['Staff Name', staff.staffName || '']);
      rows.push(['Overall Avg Attendance', staff.avgAttendancePercent !== null ? `${staff.avgAttendancePercent}%` : '—']);
      rows.push(['Date Range', `${startDate} to ${endDate}`]);
      rows.push([]);

      (staff.subjects || []).forEach((item) => {
        if (item.type === 'group') {
          rows.push(['Type', 'Combined Class']);
          rows.push(['Batch Group', item.batchGroupName || '']);
          rows.push(['Total Students', item.totalStudents]);
          rows.push(['Sessions Recorded', item.classesWithAttendance]);
          rows.push(['Avg Attending', item.avgAttendingStudents !== null ? Math.round(item.avgAttendingStudents) : '—']);
          rows.push(['Avg Attendance %', item.avgAttendancePercent !== null ? `${item.avgAttendancePercent}%` : '—']);
          rows.push([]);
          rows.push(['', 'Subject', 'Cohort', 'Batch', 'Enrolled']);
          // Group subjects by subjectName for display
          const sgMap = {};
          (item.groupSubjects || []).forEach((sub) => {
            const key = sub.subjectName || '—';
            if (!sgMap[key]) sgMap[key] = { subjectName: sub.subjectName, subjectCode: sub.subjectCode, batches: [] };
            sgMap[key].batches.push(sub);
          });
          Object.values(sgMap).forEach((sg) => {
            const sgTotalStudents = sg.batches.reduce((s, b) => s + (b.totalStudents || 0), 0);
            const sgSessions = sg.batches.reduce((max, b) => Math.max(max, b.classesWithAttendance || 0), 0);
            const effectiveSessions = sgSessions > 0 ? sgSessions : item.classesWithAttendance;
            const sgPresent = effectiveSessions > 0
              ? (sgSessions > 0
                  ? Math.round(sg.batches.reduce((s, b) => s + (b.totalPresent || 0), 0) / effectiveSessions)
                  : (item.avgAttendingStudents !== null && item.totalStudents > 0
                      ? Math.round(item.avgAttendingStudents * sgTotalStudents / item.totalStudents)
                      : null))
              : null;
            const sgAbsent = sgPresent !== null ? Math.max(0, sgTotalStudents - sgPresent) : null;
            const sgPercent = sgPresent !== null && sgTotalStudents > 0
              ? Math.round((sgPresent / sgTotalStudents) * 1000) / 10 : null;
            rows.push(['', `${sg.subjectName || '—'} (${sg.subjectCode || ''})`, '', '', '']);
            sg.batches.forEach((sub) => {
              rows.push(['', '', sub.cohortLabel || '', sub.batchName || '', sub.totalStudents]);
            });
            rows.push(['', 'Subject Total', sgTotalStudents, `Present: ${sgPresent ?? '—'}`, `Absent: ${sgAbsent ?? '—'}`, `${sgPercent !== null ? sgPercent + '%' : '—'}`]);
            rows.push([]);
          });
        } else {
          const sPresent = item.avgAttendingStudents !== null ? Math.round(item.avgAttendingStudents) : null;
          const sAbsent = sPresent !== null ? Math.max(0, item.totalStudents - sPresent) : null;
          rows.push(['Type', 'Solo']);
          rows.push(['Subject', `${item.subjectName || '—'} (${item.subjectCode || ''})` ]);
          rows.push(['Cohort', item.cohortLabel || '']);
          rows.push(['Batch', item.batchName || '']);
          rows.push(['Total Students', item.totalStudents]);
          rows.push(['Sessions Recorded', item.classesWithAttendance]);
          rows.push(['Avg Present', sPresent ?? '—']);
          rows.push(['Avg Absent', sAbsent ?? '—']);
          rows.push(['Attendance %', item.avgAttendancePercent !== null ? `${item.avgAttendancePercent}%` : '—']);
          rows.push([]);
        }
      });

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [{ wch: 22 }, { wch: 38 }, { wch: 38 }, { wch: 28 }, { wch: 14 }, { wch: 14 }];
      // Sheet name max 31 chars, strip invalid chars
      const sheetName = `${index + 1}. ${(staff.staffName || 'Staff').replace(/[:\\/?*[\]]/g, '').substring(0, 28)}`;
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    XLSX.writeFile(wb, `Attendance_${startDate}_to_${endDate}.xlsx`);
  };

  return (
    <>
    <DashboardLayout title="Average Attendance">
      <div className="mx-auto max-w-7xl px-4 py-6">
        {/* Page header */}
        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-lg bg-indigo-600 p-2 text-white">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Average Attendance</h1>
            <p className="text-sm text-gray-500">Per-staff attendance overview for a selected date range</p>
          </div>
        </div>

        {/* Date range picker */}
        <div className="mb-6 flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="min-w-[220px] flex-1">
            <label className="mb-1 block text-xs font-semibold text-gray-600">Staff Name</label>
            <input
              type="text"
              value={staffSearch}
              onChange={(e) => setStaffSearch(e.target.value)}
              placeholder="Search staff name"
              className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">Start Date</label>
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">End Date</label>
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? (
              <>
                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Loading…
              </>
            ) : (
              'View'
            )}
          </button>
          {loaded && staffAttendance.length > 0 && (
            <button
              onClick={downloadExcel}
              className="flex items-center gap-2 rounded-lg border border-emerald-600 bg-emerald-50 px-5 py-2 text-sm font-semibold text-emerald-700 shadow hover:bg-emerald-100"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
              </svg>
              Download Excel
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        {/* Results */}
        {loaded && !loading && (
          staffAttendance.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white py-16 text-center text-sm text-gray-400 shadow-sm">
              No attendance records found for the selected date range.
            </div>
          ) : filteredStaffAttendance.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white py-16 text-center text-sm text-gray-400 shadow-sm">
              No staff matched the current search.
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredStaffAttendance.map((staff, index) => {
                const rank = index + 1;
                const rating = getAttendanceRating(staff.avgAttendancePercent);
                const totalBatches = (staff.subjects || []).reduce((sum, item) =>
                  sum + (item.type === 'group' ? (item.batchCount || 0) : 1), 0);
                const totalStudents = (staff.subjects || []).reduce((sum, item) => sum + (item.totalStudents || 0), 0);
                const totalPresent = (staff.subjects || []).reduce((sum, item) =>
                  sum + (item.avgAttendingStudents !== null ? Math.round(item.avgAttendingStudents) : 0), 0);
                const avgAbsent = totalStudents - totalPresent;

                return (
                  <div key={staff.staffId} className="relative flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                    {/* Rank badge */}
                    <div className={`absolute left-0 top-0 flex h-6 w-6 items-center justify-center rounded-br-lg text-xs font-bold ${
                      rank === 1 ? 'bg-yellow-400 text-yellow-900' :
                      rank === 2 ? 'bg-gray-300 text-gray-700' :
                      rank === 3 ? 'bg-amber-600 text-white' :
                      'bg-gray-100 text-gray-500'
                    }`}>{rank}</div>
                    {/* Staff header */}
                    <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50 px-4 py-3">
                      {getStaffImageUrl(staff) ? (
                        <img src={getStaffImageUrl(staff)} alt={staff.staffName} onError={() => handleImageError(staff.staffId)} className="h-10 w-10 rounded-full object-cover ring-2 ring-indigo-200" />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-sm font-bold text-indigo-700 ring-2 ring-indigo-200">
                          {(staff.staffName || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-gray-900">{staff.staffName}</p>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1">
                          {rating && (
                            <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-semibold ${rating.style}`}>
                              {rating.label}
                            </span>
                          )}
                          {totalBatches > 0 && (
                            <span className="inline-block rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
                              {totalBatches} {totalBatches === 1 ? 'batch' : 'batches'}
                            </span>
                          )}
                        </div>
                        {totalStudents > 0 && (
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            <span className="flex items-center gap-0.5 text-xs text-gray-500">
                              <span className="font-semibold text-gray-700">{totalStudents}</span> total
                            </span>
                            <span className="text-gray-300">·</span>
                            <span className="flex items-center gap-0.5 text-xs text-emerald-600">
                              <span className="font-semibold">{totalPresent}</span> present
                            </span>
                            <span className="text-gray-300">·</span>
                            <span className="flex items-center gap-0.5 text-xs text-red-500">
                              <span className="font-semibold">{avgAbsent}</span> absent
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">
                          {staff.avgAttendancePercent !== null ? `${staff.avgAttendancePercent}%` : '—'}
                        </p>
                        <p className="text-xs text-gray-400">avg</p>
                      </div>
                    </div>

                    {/* Progress bar */}
                    {staff.avgAttendancePercent !== null && rating && (
                      <div className="px-4 pt-2">
                        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                          <div className={`h-2 rounded-full ${rating.bar}`} style={{ width: `${Math.min(staff.avgAttendancePercent, 100)}%` }} />
                        </div>
                        <p className="mt-1 text-xs italic text-gray-400">{rating.tip}</p>
                      </div>
                    )}

                    {/* View details button → opens modal */}
                    <button
                      onClick={() => setSelectedStaff(staff)}
                      className="mx-4 mb-3 mt-2 flex items-center justify-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                    >
                      View subject details
                    </button>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    </DashboardLayout>

    {/* ===== Subject Detail Modal ===== */}
    {selectedStaff && (() => {
      const modalStaff = selectedStaff;
      const modalRating = getAttendanceRating(modalStaff.avgAttendancePercent);
      const modalItems = buildDisplayItems(modalStaff.subjects);
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" onClick={() => setSelectedStaff(null)}>
          <div className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Modal header */}
            <div className="flex items-center gap-3 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4">
              {getStaffImageUrl(modalStaff) ? (
                <img src={getStaffImageUrl(modalStaff)} alt={modalStaff.staffName} onError={() => handleImageError(modalStaff.staffId)} className="h-12 w-12 rounded-full object-cover ring-2 ring-indigo-300" />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 text-base font-bold text-indigo-700 ring-2 ring-indigo-300">
                  {(modalStaff.staffName || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900">{modalStaff.staffName}</h3>
                <div className="mt-0.5 flex items-center gap-2">
                  {modalRating && (
                    <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${modalRating.style}`}>{modalRating.label}</span>
                  )}
                  <span className="text-sm font-bold text-indigo-700">
                    {modalStaff.avgAttendancePercent !== null ? `${modalStaff.avgAttendancePercent}% avg` : '—'}
                  </span>
                </div>
              </div>
              <button onClick={() => setSelectedStaff(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal body */}
            <div className="flex-1 space-y-3 overflow-y-auto px-6 py-4">
              {modalItems.map((item, idx) => {
                if (item.type === 'group') {
                  // Group subjects by subjectName
                  const subjectGroups = [];
                  const subjectIndexMap = {};
                  (item.groupSubjects || []).forEach((sub) => {
                    const key = sub.subjectName || '—';
                    if (subjectIndexMap[key] === undefined) {
                      subjectIndexMap[key] = subjectGroups.length;
                      subjectGroups.push({ subjectName: sub.subjectName, subjectCode: sub.subjectCode, batches: [] });
                    }
                    subjectGroups[subjectIndexMap[key]].batches.push(sub);
                  });

                  return (
                    <div key={`mg-${item.batchGroupId}-${idx}`} className="rounded-lg border-2 border-indigo-200 bg-indigo-50 p-3">
                      <div className="mb-2 flex items-center gap-2">
                        <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-xs font-bold text-white">Combined class</span>
                        <span className="text-xs font-semibold text-indigo-700">{item.batchGroupName || 'Batch Group'}</span>
                        {item.batchCount > 0 && (
                          <span className="rounded-full bg-indigo-100 border border-indigo-300 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                            {item.batchCount} {item.batchCount === 1 ? 'batch' : 'batches'}
                          </span>
                        )}
                      </div>
                      {subjectGroups.map((sg) => {
                        const sgTotalStudents = sg.batches.reduce((s, b) => s + (b.totalStudents || 0), 0);
                        const sgSessions = sg.batches.reduce((max, b) => Math.max(max, b.classesWithAttendance || 0), 0);
                        const sgRawPresent = sg.batches.reduce((s, b) => s + (b.totalPresent || 0), 0);

                        // If no direct session data for this subject, fall back to group-level proportional estimate.
                        // Combined class schedules use one batchSubjectId for all subjects — siblings have no direct rows.
                        const effectiveSessions = sgSessions > 0 ? sgSessions : item.classesWithAttendance;
                        const sgPresent = effectiveSessions > 0
                          ? (sgSessions > 0
                              ? Math.round(sgRawPresent / effectiveSessions)
                              : (item.avgAttendingStudents !== null && item.totalStudents > 0
                                  ? Math.round(item.avgAttendingStudents * sgTotalStudents / item.totalStudents)
                                  : null))
                          : null;
                        const sgAbsent = sgPresent !== null ? Math.max(0, sgTotalStudents - sgPresent) : null;
                        const sgPercent = sgPresent !== null && sgTotalStudents > 0
                          ? Math.round((sgPresent / sgTotalStudents) * 1000) / 10 : null;
                        const sgRating = getAttendanceRating(sgPercent);
                        return (
                          <div key={sg.subjectName} className="mb-2 rounded-md border border-indigo-200 bg-white shadow-sm">
                            {/* Subject header */}
                            <div className="border-b border-indigo-100 bg-indigo-50 px-3 py-1.5">
                              <p className="text-xs font-bold text-indigo-800">
                                {sg.subjectName || '—'}{' '}
                                <span className="font-normal text-indigo-400">{sg.subjectCode ? `(${sg.subjectCode})` : ''}</span>
                              </p>
                            </div>
                            {/* Batch rows */}
                            {sg.batches.map((sub) => (
                              <div key={sub.batchSubjectId} className="border-b border-gray-100 px-3 py-2 text-xs last:border-0">
                                {(() => {
                                  const subjectStatus = getResolvedBatchSubjectStatus(sub);
                                  return (
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="text-gray-600">{sub.cohortLabel}</p>
                                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${getBatchSubjectStatusClassName(subjectStatus)}`}>
                                    {formatBatchSubjectStatus(subjectStatus)}
                                  </span>
                                </div>
                                  );
                                })()}
                                {sub.batchName && <p className="text-gray-400">Batch: {sub.batchName}</p>}
                                <p className="mt-0.5 font-semibold text-gray-500">{sub.totalStudents} enrolled</p>
                              </div>
                            ))}
                            {/* Per-subject attendance stats */}
                            {sgTotalStudents > 0 && (
                              <div className="border-t border-indigo-100 bg-indigo-50/60 px-3 py-2">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-xs font-semibold text-gray-700">
                                      Avg {sgPresent !== null ? sgPresent : '—'} / {sgTotalStudents} attending
                                    </p>
                                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                                      <span className="text-xs text-gray-500"><span className="font-semibold text-gray-700">{sgTotalStudents}</span> total</span>
                                      <span className="text-gray-300">·</span>
                                      <span className="text-xs text-emerald-600"><span className="font-semibold">{sgPresent !== null ? sgPresent : '—'}</span> present</span>
                                      <span className="text-gray-300">·</span>
                                      <span className="text-xs text-red-500"><span className="font-semibold">{sgAbsent !== null ? sgAbsent : '—'}</span> absent</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    {sgRating && (
                                      <span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${sgRating.style}`}>{sgRating.label}</span>
                                    )}
                                    <span className="text-sm font-bold text-gray-800">{sgPercent !== null ? `${sgPercent}%` : '—'}</span>
                                  </div>
                                </div>
                                {sgPercent !== null && sgRating && (
                                  <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                                    <div className={`h-1.5 rounded-full ${sgRating.bar}`} style={{ width: `${Math.min(sgPercent, 100)}%` }} />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                // Solo subject
                const sub = item;
                const subjectStatus = getResolvedBatchSubjectStatus(sub);
                const sRating = getAttendanceRating(sub.avgAttendancePercent);
                const sPresent = sub.avgAttendingStudents !== null ? Math.round(sub.avgAttendingStudents) : null;
                const sAbsent = sPresent !== null ? Math.max(0, sub.totalStudents - sPresent) : null;
                return (
                  <div key={`ms-${sub.batchSubjectId}`} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          {sub.subjectName || '—'}{' '}
                          <span className="font-normal text-gray-400 text-xs">{sub.subjectCode ? `(${sub.subjectCode})` : ''}</span>
                        </p>
                        <p className="text-xs text-gray-500">{sub.cohortLabel}</p>
                        {sub.batchName && <p className="text-xs text-gray-400">Batch: {sub.batchName}</p>}
                      </div>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${getBatchSubjectStatusClassName(subjectStatus)}`}>
                        {formatBatchSubjectStatus(subjectStatus)}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-gray-700">
                          Avg {sPresent !== null ? sPresent : '—'} / {sub.totalStudents} attending
                        </p>
                        <p className="text-xs text-gray-400">
                          {sub.classesWithAttendance} session{sub.classesWithAttendance !== 1 ? 's' : ''} recorded
                        </p>
                        {sub.totalStudents > 0 && (
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            <span className="text-xs text-gray-500"><span className="font-semibold text-gray-700">{sub.totalStudents}</span> total</span>
                            <span className="text-gray-300">·</span>
                            <span className="text-xs text-emerald-600"><span className="font-semibold">{sPresent !== null ? sPresent : '—'}</span> present</span>
                            <span className="text-gray-300">·</span>
                            <span className="text-xs text-red-500"><span className="font-semibold">{sAbsent !== null ? sAbsent : '—'}</span> absent</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {sRating && (
                          <span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${sRating.style}`}>{sRating.label}</span>
                        )}
                        <span className="text-sm font-bold text-gray-800">
                          {sub.avgAttendancePercent !== null ? `${sub.avgAttendancePercent}%` : '—'}
                        </span>
                      </div>
                    </div>
                    {sub.avgAttendancePercent !== null && sRating && (
                      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                        <div className={`h-1.5 rounded-full ${sRating.bar}`} style={{ width: `${Math.min(sub.avgAttendancePercent, 100)}%` }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Modal footer */}
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-3 text-right">
              <button onClick={() => setSelectedStaff(null)} className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white hover:bg-indigo-700">
                Close
              </button>
            </div>
          </div>
        </div>
      );
    })()}
    </>
  );
}
